import React, { useState } from 'react';
import { FormModal, Field, inputCls, btnGhost } from './FormKit';
import ExportExcelButton from './ExportExcelButton';
import { provenanceOf } from './provenance';
import { fetchJSON } from '../../utils/api';
import { formatDate } from '../../utils/dateFormat';
import { firmToday } from '../../utils/dateRange';
import { exportToExcel } from '../../utils/excelExport';
import { serviceLabel } from '../../utils/engagementName';
import { pieText } from '../../utils/engagementProfile';
import { engagementNo } from '../../utils/engagementNo';
import { qcEstimatedFeeOf, qcNasFeeOf } from '../../pages/qcQuestions';

// Management → Engagements 的下載：選 FYE 區間（最多兩年，後端同樣把關）與
// Status，每案一列、欄位照詳情頁的順序（GET /api/engagements/export）。
// 名字是寫入當時的快照，主檔改過名就接「(now: …)」，與畫面同規則。

const MAX_YEARS = 2;

const d = (v) => (v ? formatDate(v) : '');
const yn = (v) => (v === true ? 'Yes' : v === false ? 'No' : '');
const txt = (v) => (v === null || v === undefined ? '' : String(v));
const withNow = (value, now) => (value ? (now ? `${value} (now: ${now})` : value) : '');
const names = (list, single, singleNow) => ((list || []).some((m) => m && m.staffName)
  ? list.map((m) => withNow(m.staffName, m.staffNameNow)).join('; ')
  : withNow(single, singleNow));
// 記錄從哪來：申請單號；沒有單號就是匯入或 Management 建的。
const recordRef = (rec) => {
  if (!rec) return '';
  if (rec.sourceRequestNo) return rec.sourceRequestNo;
  return provenanceOf(rec.source) === 'import' ? 'Imported' : 'Management';
};

// Estimated Fees：QC 表單有費用就以表單為準，否則用建案時填的金額——同詳情頁。
const estimatedFee = (e) => {
  const qc = e.qcRecord;
  const fee = qc ? qcEstimatedFeeOf(qc.answers, qc.qcType, { engagementId: e.id, covered: qc.engagements || [] }) : null;
  if (fee) return { currency: fee.currency, amount: fee.amount, group: fee.scope === 'group' };
  if (e.proposedFees == null) return { currency: '', amount: '', group: false };
  return { currency: e.proposedFeesCcy || 'SGD', amount: e.proposedFees, group: !!e.proposedFeesShared };
};

const nasFee = (e) => {
  const qc = e.qcRecord;
  const fee = qc ? qcNasFeeOf(qc.answers, qc.qcType) : null;
  if (!fee) return '';
  if (fee.text) return fee.text;
  return fee.none ? 'No NAS' : `${fee.currency} ${fee.amount.toLocaleString()}`;
};

const excelRow = (e) => {
  const fee = estimatedFee(e);
  const entity = e.entityName ? withNow(e.entityName, e.entityNameNow) : '';
  return {
    'File Ref.': engagementNo(e.id) || '',
    'Client Group': withNow(e.clientGroup, e.clientGroupNow),
    Entity: entity,
    UEN: withNow(e.uen, e.uenNow),
    'Year End (FYE)': d(e.yearEnd),
    Status: txt(e.status),
    'Current Stage': (e.currentStage && e.currentStage.label) || '',
    'Service Provided': txt(serviceLabel(e.serviceProvided, e.engagementSeq)),
    'Description of Service': txt(e.descriptionOfService),
    'Date of Appointment': d(e.dateOfAppointment),
    'Estimated Fees Currency': fee.currency,
    'Estimated Fees': fee.amount,
    'Estimated Fees Is a Group Total': fee.amount === '' ? '' : yn(fee.group),
    'Non-audit Services (NAS) Fee': nasFee(e),
    Source: recordRef(e),
    'Partner in Charge': names(e.partners, e.partner, e.partnerNow),
    'Director(s)': names(e.directors, e.director, e.directorNow),
    'Manager(s)': names(e.managers, e.manager, e.managerNow),
    EQR: withNow(e.eqr, e.eqrNow),
    'Team Members': (e.teamMembers || []).map((m) => withNow(m.staffName, m.staffNameNow)).join('; '),
    'Public Interest Entity (PIE)': txt(pieText(e)),
    'Ultimate Parent': txt(e.ultimateParent),
    'SSIC Code Primary': txt(e.ssicPrimary),
    'SSIC Code Secondary': txt(e.ssicSecondary),
    'Principal Activity 1': txt(e.principalActivity1),
    'Principal Activity 2': txt(e.principalActivity2),
    'Overseas Operations': yn(e.overseasOperations),
    'Consolidated FS': e.consolidatedFs === 'yes' ? 'Yes' : e.consolidatedFs === 'no' ? 'No' : '',
    'SEC Registered': yn(e.secRegistered),
    'Group Audit': yn(e.isGroupAudit),
    'Under Investigation': yn(e.underInvestigation),
    'Fee Information Communicated to TCWG': yn(e.tcwgFeeCommunicated),
    'QM Screening': yn(e.screeningRequired),
    'Transnational Audit (TNA)': yn(e.isTna),
    'Transnational Entity (TNE)': yn(e.isTne),
    'Listed Company': yn(e.isListed),
    CaseWare: e.casewareOpened ? 'Activated' : 'Not activated',
    'Charge Code': e.chargeCodeAssigned ? 'Activated' : 'Not activated',
    'QC Form': e.qcRecord ? e.qcRecord.qcType : '',
    'QC Reference': recordRef(e.qcRecord),
    'Current Risk Rating': (e.qcRecord && e.qcRecord.riskRating) || '',
    'Expected FS Signing Date': d(e.expectedFsSigningDate),
    'Sign Off Date': d(e.signOffDate),
    'Signed FS Reference': recordRef(e.fsRecord),
    'Audit Opinion': (e.fsRecord && e.fsRecord.auditOpinion) || '',
    'Archival Deadline': d(e.archivalDeadline),
    'Archival Date': d(e.archivalDate),
    'QC35 Reference': recordRef(e.qc35Record),
    Remarks: txt(e.remarks),
  };
};

// 預設區間：去年 1 月 1 日到今年 12 月 31 日——剛好兩年。
const defaultRange = () => {
  const y = Number(firmToday().slice(0, 4));
  return { from: `${y - 1}-01-01`, to: `${y}-12-31` };
};

// 與後端同一條：to 必須早於 from 加兩年。
const rangeError = (from, to) => {
  if (!from || !to) return 'Choose both FYE dates.';
  if (from > to) return 'The FYE from date must be on or before the to date.';
  const [y, m, day] = from.split('-');
  if (to >= `${Number(y) + MAX_YEARS}-${m}-${day}`) return `The FYE range can be at most ${MAX_YEARS} years.`;
  return null;
};

const EngagementExportModal = ({ statusOptions = [], initialStatuses = [], onClose }) => {
  const [range, setRange] = useState(defaultRange);
  // 預設沿用清單上勾的 Status；清單沒勾（＝全部）就全勾。
  const [statuses, setStatuses] = useState(initialStatuses.length > 0 ? initialStatuses : statusOptions);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const invalid = rangeError(range.from, range.to) || (statuses.length === 0 ? 'Choose at least one status.' : null);
  const toggle = (s) => setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams({ fyeFrom: range.from, fyeTo: range.to, statuses: statuses.join(',') });
      const data = await fetchJSON(`/api/engagements/export?${params}`);
      const rows = (data.data || []).map(excelRow);
      if (rows.length === 0) { setError('No engagements match these filters.'); return; }
      exportToExcel(rows, 'Engagements.xlsx');
      onClose();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <FormModal
      title="Export Engagements"
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <ExportExcelButton onClick={download} disabled={!!invalid} busy={busy} />
        </>
      )}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="FYE From" required>
          <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className={inputCls} />
        </Field>
        <Field label="FYE To" required>
          <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className={inputCls} />
        </Field>
      </div>
      <p className="text-xs text-slate-500 -mt-2">The range can be at most {MAX_YEARS} years.</p>
      <Field label="Status" required>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {statusOptions.map((s) => (
            <label key={s} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={statuses.includes(s)} onChange={() => toggle(s)} />
              {s}
            </label>
          ))}
        </div>
      </Field>
      {(error || (invalid && range.from && range.to)) && (
        <p className="text-sm text-red-600">{error || invalid}</p>
      )}
    </FormModal>
  );
};

export default EngagementExportModal;
